'use strict';

var _ = require('underscore'),
    ActionTypes = require('../constants/ActionTypes'),
    AppDispatcher = require('../AppDispatcher'),
    SidebarActionCreators = require('./SidebarActionCreators'),
    AlertActionCreators = require('./AlertActionCreators'),
    NotificationActionCreators = require('./NotificationActionCreators'),
    NotificationMessages = require('../constants/NotificationMessages'),
    AlertTypes = require('../constants/AlertTypes'),
    UserStore = require('../stores/UserStore'),
    RouterStore = require('../stores/RouterStore'),
    { replaceWith, makeHref } = require('../router');

function makeErrorAlert(err) {
  var { body, status } = err;

  if (!status) {
    return { id: AlertTypes.CONNECTION_ERROR };
  }

  if (!body) {
    return { id: AlertTypes.SERVER_ERROR };
  }

  var firstErrorKey = Object.keys(body).filter(key => body[key])[0];
  if (!firstErrorKey) {
    return { id: AlertTypes.SERVER_ERROR };
  }

  var message = body[firstErrorKey].message;
  return {
    id: message.id,
    options: _.omit(message, 'id')
  };
}

var ZineServerActionCreators = {
  handleZine(err, response, zineSlug) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REQUEST_ZINE_ERROR : ActionTypes.REQUEST_ZINE_SUCCESS,
      response: response,
      zineSlug: zineSlug
    });

    if (err) {
      console.error(err);

      var params = RouterStore.getRouterState().params;
      if (err.status === 404 && params.zineSlug === zineSlug) {
        AlertActionCreators.showAlert(AlertTypes.NOT_FOUND);
      }
    }
  },

  handleCreateZine(err, response) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.CREATE_ZINE_ERROR : ActionTypes.CREATE_ZINE_SUCCESS,
      response: response,
      zineSlug: err ? null : response.result
    });

    if (err) {
      var { id, options } = makeErrorAlert(err);
      AlertActionCreators.showAlert(id, options);
    } else {
      replaceWith('zine', {
        zineSlug: response.result
      });
    }
  },

  handleUpdateZine(err, response, zine) {
    if (!err && response.result !== zine.slug) {
      // Bail out of reconciling when slug changes
      window.location = makeHref('zine', {
        zineSlug: response.result
      });
      return;
    }

    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.UPDATE_ZINE_ERROR : ActionTypes.UPDATE_ZINE_SUCCESS,
      response: response,
      zine: zine
    });

    if (err) {
      var { id, options } = makeErrorAlert(err);
      AlertActionCreators.showAlert(id, options);
    }
  },

  handleFollowZine(err, response, zineSlug) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.FOLLOW_ZINE_ERROR : ActionTypes.FOLLOW_ZINE_SUCCESS,
      response: response,
      zineSlug: zineSlug
    });

    if (err) {
      console.error(err);
    } else {
      NotificationActionCreators.showNotification(NotificationMessages.ZINE_FOLLOWED, {
        zineSlug: zineSlug
      });
    }
  },

  handleUnfollowZine(err, response, zineSlug) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.UNFOLLOW_ZINE_ERROR : ActionTypes.UNFOLLOW_ZINE_SUCCESS,
      response: response,
      zineSlug: zineSlug
    });

    if (err) {
      console.error(err);
    }
  },

  handleDeleteZine(err, response, zineSlug) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.DELETE_ZINE_ERROR : ActionTypes.DELETE_ZINE_SUCCESS,
      response: response,
      zineSlug: zineSlug
    });

    if (err) {
      console.error(err);
    } else {
      // Bail out of reconciling changes
      window.location = makeHref('user', {
        userId: UserStore.getMe().id
      });
    }
  },

  handleSubmitStampToZine(err, response, zineSlug, stampSegment) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.SUBMIT_STAMP_TO_ZINE_ERROR : ActionTypes.SUBMIT_STAMP_TO_ZINE_SUCCESS,
      response: response,
      zineSlug: zineSlug,
      stampSegment: stampSegment
    });

    if (err) {
      console.error(err);
    } else {
      NotificationActionCreators.showNotification(
        NotificationMessages.STAMP_SUBMITTED_TO_ZINE,
        { zineSlug }
      );
    }
  },

  handleAddStampToZine(err, response, zineSlug, stampSegment) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.ADD_STAMP_TO_ZINE_ERROR : ActionTypes.ADD_STAMP_TO_ZINE_SUCCESS,
      response: response,
      zineSlug: zineSlug,
      stampSegment: stampSegment,
      zineStampId: err ? null : response.result
    });

    if (err) {
      console.error(err);
    }
  },

  handleRemoveStampFromZine(err, response, zineSlug, stampSegment) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REMOVE_STAMP_FROM_ZINE_ERROR : ActionTypes.REMOVE_STAMP_FROM_ZINE_SUCCESS,
      response: response,
      zineSlug: zineSlug,
      stampSegment: stampSegment
    });

    if (err) {
      console.error(err);
    }
  },

  handleApproveStampToZine(err, response, zineSlug, stampSegment) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.APPROVE_STAMP_TO_ZINE_ERROR : ActionTypes.APPROVE_STAMP_TO_ZINE_SUCCESS,
      response: response,
      zineSlug: zineSlug,
      stampSegment: stampSegment,
      zineStampId: err ? null : response.result
    });

    if (err) {
      console.error(err);
    } else {
      SidebarActionCreators.hideSidebar();

      NotificationActionCreators.showNotification(
        NotificationMessages.STAMP_APPROVED_TO_ZINE,
        { zineSlug }
      );
    }
  },

  handleRejectStampFromZine(err, response, zineSlug, stampSegment, message) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REJECT_STAMP_FROM_ZINE_ERROR : ActionTypes.REJECT_STAMP_FROM_ZINE_SUCCESS,
      response: response,
      zineSlug: zineSlug,
      stampSegment: stampSegment,
      message: message
    });

    if (err) {
      console.error(err);
    } else {
      SidebarActionCreators.hideSidebar();

      NotificationActionCreators.showNotification(
        NotificationMessages.STAMP_DECLINED_FROM_ZINE,
        { zineSlug }
      );
    }
  },

  handleRequestZineSections(err, response) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REQUEST_ZINE_SECTIONS_ERROR : ActionTypes.REQUEST_ZINE_SECTIONS_SUCCESS,
      response: response
    });

    if (err) {
      console.error(err);
    }
  },

  handleRequestZinesInSection(err, response, sectionId) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REQUEST_ZINES_IN_SECTION_ERROR : ActionTypes.REQUEST_ZINES_IN_SECTION_SUCCESS,
      response: response,
      sectionId: sectionId
    });

    if (err) {
      console.error(err);
    }
  },

  handleSuggestedPage(err, response) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REQUEST_SUGGESTED_ZINES_ERROR : ActionTypes.REQUEST_SUGGESTED_ZINES_SUCCESS,
      response: response
    });

    if (err) {
      console.error(err);
    }
  },

  handleUserCuratedPage(err, response, userId) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REQUEST_USER_CURATED_ZINES_ERROR : ActionTypes.REQUEST_USER_CURATED_ZINES_SUCCESS,
      response: response,
      userId: userId
    });

    if (err) {
      console.error(err);
    }
  },

  handleUserCuratedUnlistedPage(err, response, userId) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REQUEST_USER_CURATED_UNLISTED_ZINES_ERROR : ActionTypes.REQUEST_USER_CURATED_UNLISTED_ZINES_SUCCESS,
      response: response,
      userId: userId
    });

    if (err) {
      console.error(err);
    }
  },

  handleUserContributedPage(err, response, userId) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REQUEST_USER_CONTRIBUTED_ZINES_ERROR : ActionTypes.REQUEST_USER_CONTRIBUTED_ZINES_SUCCESS,
      response: response,
      userId: userId
    });

    if (err) {
      console.error(err);
    }
  },

  handleUserContributedUnlistedPage(err, response, userId) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REQUEST_USER_CONTRIBUTED_UNLISTED_ZINES_ERROR : ActionTypes.REQUEST_USER_CONTRIBUTED_UNLISTED_ZINES_SUCCESS,
      response: response,
      userId: userId
    });

    if (err) {
      console.error(err);
    }
  },

  handleSearchPage(err, response, terms) {
    AppDispatcher.handleServerAction({
      type: err ? ActionTypes.REQUEST_ZINE_SEARCH_ERROR : ActionTypes.REQUEST_ZINE_SEARCH_SUCCESS,
      terms: terms,
      response: response
    });

    if (err) {
      console.error(err);
    }
  }
};

module.exports = ZineServerActionCreators;


/** WEBPACK FOOTER **
 ** ./actions/ZineServerActionCreators.js
 **/